import AchievementNotifications from '@/components/Achievements/AchievementNotifications';
import Analytics from '@/components/Analytics/Analytics';
import CachedCardMediaContext, {
	cachedCardMediaDefaultValue,
} from '@/context/CachedCardMediaContext';
import RememberScrollPositionContext, {
	rememberScrollPositionDefaultValue,
} from '@/context/RememberScrollPositionContext';
import OptiesErrorsRoute from '@/routes/opties/errors/OptiesErrorsRoute';
import OptiesRoute from '@/routes/opties/OptiesRoute';
import RecorderRoute from '@/routes/recorder/RecorderRoute';
import SpeciesRoute from '@/routes/soort/[id]/SpeciesRoute';
import UnsupportedRoute from '@/routes/unsupported/UnsupportedRoute';
import { ResizeObserver as Polyfill } from '@juggle/resize-observer';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
import { AppUpsell } from './components/AppUpsell/AppUpsell';
import DeepLink from './components/DeepLink/DeepLink';
import SplashScreen from './components/SplashScreen/SplashScreen';
import './index.css';
import { CalendarProvider } from './resources/calendar';
import { CardsProvider } from './resources/cards';
import { CollectionsProvider } from './resources/collections';
import { CurrentDateProvider } from './resources/currentDate';
import { SeasonProvider } from './resources/seasons';
import { SpeciesProvider } from './resources/species';
import CameraRoute from './routes/camera/CameraRoute';
import IndexRoute from './routes/IndexRoute';
import OepsRoute from './routes/oeps/OepsRoute';

// Polyfill ResizeObserver
window.ResizeObserver = window.ResizeObserver ?? Polyfill;

// Reload on update
const updateSW = registerSW({
	onNeedRefresh: () => {
		// Set the isUpdating key in localStorage
		localStorage.setItem('soortsafari-isUpdating', 'true');
		// Add the isUpdating search parameter to the URL
		const url = new URL(location.href);
		url.searchParams.set('isUpdating', 'true');
		location.replace(url.toString());
		setTimeout(() => {
			updateSW(true);
		}, 1000);
	},
});

// eslint-disable-next-line react-refresh/only-export-components
const BaseLayout = () => {
	return (
		// Provides the (custom) current date
		<CurrentDateProvider>
			{/* Provides the season theme */}
			<SeasonProvider>
				{/* Shows the splash screen */}
				<SplashScreen>
					{/* Provides the species functionality */}
					<SpeciesProvider>
						{/* Provides the calendar functionality */}
						<CalendarProvider>
							{/* Provides the collections functionality */}
							<CollectionsProvider>
								{/* Provides the cards functionality */}
								<CardsProvider>
									{/* Initializes analytics */}
									<Analytics />
									{/* Shows the achievements notifications */}
									<AchievementNotifications />
									{/* Opens deep links in app */}
									<DeepLink />
									{/* Shows the app upsell if not in native app */}
									<AppUpsell />
									{/* Provides remembered scroll position for home tabs */}
									<RememberScrollPositionContext.Provider
										value={
											rememberScrollPositionDefaultValue
										}>
										{/* Provides the cached media */}
										<CachedCardMediaContext.Provider
											value={cachedCardMediaDefaultValue}>
											{/* Shows the page component */}
											<Outlet />
										</CachedCardMediaContext.Provider>
									</RememberScrollPositionContext.Provider>
								</CardsProvider>
							</CollectionsProvider>
						</CalendarProvider>
					</SpeciesProvider>
				</SplashScreen>
			</SeasonProvider>
		</CurrentDateProvider>
	);
};

const router = createBrowserRouter([
	{
		element: <BaseLayout />,
		children: [
			{
				path: '/',
				element: <IndexRoute />,
			},
			{
				path: '/camera',
				element: <CameraRoute />,
			},
			{
				path: '/oeps',
				element: <OepsRoute />,
			},
			{
				path: '/recorder',
				element: <RecorderRoute />,
			},
			{
				path: '/soort/:id',
				element: <SpeciesRoute />,
			},
			{
				path: '/opties',
				element: <OptiesRoute />,
			},
			{
				path: '/opties/errors',
				element: <OptiesErrorsRoute />,
			},
			{
				path: '/unsupported',
				element: <UnsupportedRoute />,
			},
		],
	},
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
	<React.StrictMode>
		<RouterProvider router={router} />
	</React.StrictMode>,
);
